import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface PdfViewOptions {
  /** Filename for download */
  fileName?: string;
  /** Whether to open in new tab/window (default: true) */
  openInNewTab?: boolean;
  /** Whether to force download instead of preview */
  forceDownload?: boolean;
  /** Server endpoint URL for streaming fallback */
  serverUrl?: string;
  /** Additional headers for server request */
  headers?: { [key: string]: string };
  /** Callback when PDF is successfully opened */
  onSuccess?: () => void;
  /** Callback when PDF opening fails */
  onError?: (error: string) => void;
  /** Prefer iframe over location.href on iOS (default: false for openInNewTab=true, true for openInNewTab=false) */
  preferIframeOnIOS?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PdfHandlerService {
  private readonly MAX_DATA_URL_SIZE = 2 * 1024 * 1024;

  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  isIOS(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    
    return isIOSDevice || isIPadOS;
  }

  isSafari(): boolean {
    const userAgent = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
  }

  isChromeIOS(): boolean {
    const userAgent = navigator.userAgent;
    return /CriOS/i.test(userAgent) || (/Chrome/i.test(userAgent) && this.isIOS());
  }

  viewPdf(
    pdfData: Blob | string | ArrayBuffer,
    options: PdfViewOptions = {}
  ): Observable<boolean> {
    const {
      fileName = `document_${Date.now()}.pdf`,
      openInNewTab = true,
      forceDownload = false,
      serverUrl,
      preferIframeOnIOS,
      onSuccess,
      onError
    } = options;

    const isIOSDevice = this.isIOS();

    const blobPromise = this.toBlob(pdfData);

    return from(blobPromise).pipe(
      map((blob) => {
        try {
          if (isIOSDevice && serverUrl && !forceDownload) {
            if (this.openViaServerStream(serverUrl, fileName, options.headers)) {
              onSuccess?.();
              return true;
            }
          }

          if (forceDownload || (isIOSDevice && !serverUrl)) {
            this.downloadPdf(blob, fileName, options);
            onSuccess?.();
            return true;
          }

          // iOS için: Küçük dosyalarda önce iframe deneme (sayfadan çıkmaz)
          // Büyük dosyalarda direkt window.location.href (daha güvenilir)
          if (isIOSDevice) {
            // preferIframeOnIOS belirtilmişse veya openInNewTab=false ise iframe öncelikli
            const shouldPreferIframe = preferIframeOnIOS !== undefined 
              ? preferIframeOnIOS 
              : !openInNewTab;
            
            // Küçük dosyalar için iframe öncelikli (daha iyi UX - sayfadan çıkmaz)
            if (blob.size < this.MAX_DATA_URL_SIZE && shouldPreferIframe) {
              // Önce iframe ile deneme (sayfadan çıkmaz)
              this.openViaFileReader(blob, fileName, onSuccess, (error) => {
                // Iframe başarısız olursa fallback: window.location.href
                console.warn('Iframe method failed, falling back to location.href:', error);
                this.openViaDataUrl(blob, fileName, false, onSuccess, onError);
              });
              return true;
            }
            
            // Büyük dosyalar veya yeni sekme isteniyorsa direkt dataUrl
            if (blob.size < this.MAX_DATA_URL_SIZE) {
              this.openViaDataUrl(blob, fileName, openInNewTab, onSuccess, onError);
              return true;
            }
            
            // Çok büyük dosyalar için iframe (son çare)
            this.openViaFileReader(blob, fileName, onSuccess, onError);
            return true;
          }

          if (this.openViaBlobUrl(blob, fileName, openInNewTab)) {
            onSuccess?.();
            return true;
          }

          this.downloadPdf(blob, fileName, options);
          onSuccess?.();
          return true;

        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error';
          console.error('PDF view error:', errorMsg);
          onError?.(errorMsg);
          
          try {
            this.downloadPdf(blob, fileName, options);
            return true;
          } catch (downloadError) {
            onError?.(downloadError instanceof Error ? downloadError.message : 'Failed to open or download PDF');
            return false;
          }
        }
      }),
      catchError((error) => {
        const errorMsg = error instanceof Error ? error.message : 'Failed to process PDF';
        console.error('PDF processing error:', errorMsg);
        onError?.(errorMsg);
        return of(false);
      })
    );
  }
  private openViaServerStream(
    serverUrl: string,
    fileName: string,
    headers?: { [key: string]: string }
  ): boolean {
    try {
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = serverUrl;
      form.target = '_blank';
      form.style.display = 'none';
      
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      
      return true;
    } catch (error) {
      console.warn('Server stream method failed, trying alternative:', error);
      return false;
    }
  }

  private openViaDataUrl(
    blob: Blob,
    fileName: string,
    openInNewTab: boolean,
    onSuccess?: () => void,
    onError?: (error: string) => void
  ): void {
    try {
      const reader = new FileReader();
      const isIOSDevice = this.isIOS();

      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        
        if (dataUrl) {
          try {
            // iOS için özel işlem: window.open ve anchor target="_blank" çalışmaz
            if (isIOSDevice) {
              // iOS'ta data URL ile yeni sekme açmak teknik olarak mümkün değil
              // iOS'un güvenlik kısıtlamaları nedeniyle window.open ve anchor target="_blank" 
              // data URL'lerle çalışmaz
              
              // En güvenilir çözüm: window.location.href ile direkt yönlendirme
              // Kullanıcı geri butonuyla geri dönebilir
              // NOT: Bu kullanıcıyı mevcut sayfadan uzaklaştırır ama PDF kesinlikle açılır
              
              // iOS'ta state kaybını önlemek için sessionStorage'a kaydet
              try {
                const stateData = {
                  timestamp: Date.now(),
                  fileName: fileName,
                  returnUrl: window.location.href
                };
                sessionStorage.setItem('pdfViewState', JSON.stringify(stateData));
              } catch (e) {
                // sessionStorage hatası, devam et
                console.warn('Could not save state to sessionStorage:', e);
              }
              
              window.location.href = dataUrl;
              onSuccess?.();
              return;
            }
            
            // Android/Desktop için normal işlem
            if (openInNewTab) {
              const newWindow = window.open('', '_blank');
              if (newWindow) {
                newWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <meta charset="UTF-8">
                      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes">
                      <title>${fileName}</title>
                      <style>
                        * {
                          margin: 0;
                          padding: 0;
                          box-sizing: border-box;
                        }
                        html, body {
                          width: 100%;
                          height: 100%;
                          overflow: hidden;
                        }
                        embed, iframe {
                          width: 100vw;
                          height: 100vh;
                          border: none;
                          display: block;
                        }
                        @media screen and (max-width: 768px) {
                          embed, iframe {
                            width: 100%;
                            height: 100%;
                            position: fixed;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                          }
                        }
                      </style>
                    </head>
                    <body>
                      <embed src="${dataUrl}" type="application/pdf" width="100%" height="100%" />
                    </body>
                  </html>
                `);
                newWindow.document.close();
                onSuccess?.();
              } else {
                window.location.href = dataUrl;
                onSuccess?.();
              }
            } else {
              window.location.href = dataUrl;
              onSuccess?.();
            }
          } catch (e) {
            const errorMsg = e instanceof Error ? e.message : 'Data URL navigation failed';
            console.warn('Data URL navigation failed:', e);
            onError?.(errorMsg);
          }
        } else {
          onError?.('Failed to create data URL');
        }
      };

      reader.onerror = () => {
        onError?.('FileReader error while creating data URL');
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Data URL method failed';
      console.warn('Data URL method failed:', error);
      onError?.(errorMsg);
    }
  }

  private openViaFileReader(
    blob: Blob,
    fileName: string,
    onSuccess?: () => void,
    onError?: (error: string) => void
  ): void {
    try {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        if (!dataUrl) {
          onError?.('Failed to create data URL');
          return;
        }

        try {
          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.top = '0';
          iframe.style.left = '0';
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.border = 'none';
          iframe.style.zIndex = '9999';
          iframe.style.backgroundColor = '#fff';
          iframe.src = dataUrl;
          
          // iOS kontrolü ve timeout için değişkenler
          let loadTimeout: any;
          const isIOSDevice = this.isIOS();
          
          // Add close button with better styling
          const closeBtn = document.createElement('button');
          closeBtn.textContent = '✕';
          closeBtn.setAttribute('aria-label', 'Kapat');
          closeBtn.style.position = 'fixed';
          closeBtn.style.top = '10px';
          closeBtn.style.right = '10px';
          closeBtn.style.zIndex = '10000';
          closeBtn.style.padding = '12px 18px';
          closeBtn.style.fontSize = '24px';
          closeBtn.style.fontWeight = 'bold';
          closeBtn.style.cursor = 'pointer';
          closeBtn.style.backgroundColor = 'rgba(0,0,0,0.7)';
          closeBtn.style.color = 'white';
          closeBtn.style.border = 'none';
          closeBtn.style.borderRadius = '50%';
          closeBtn.style.width = '44px';
          closeBtn.style.height = '44px';
          closeBtn.style.display = 'flex';
          closeBtn.style.alignItems = 'center';
          closeBtn.style.justifyContent = 'center';
          closeBtn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
          closeBtn.style.transition = 'background-color 0.2s';
          
          // iOS'ta dokunma için daha büyük alan
          if (isIOSDevice) {
            closeBtn.style.minWidth = '44px';
            closeBtn.style.minHeight = '44px';
          }
          
          // Hover effect (desktop)
          closeBtn.onmouseenter = () => {
            closeBtn.style.backgroundColor = 'rgba(0,0,0,0.9)';
          };
          closeBtn.onmouseleave = () => {
            closeBtn.style.backgroundColor = 'rgba(0,0,0,0.7)';
          };
          
          // Close button click handler
          const closeHandler = () => {
            if (loadTimeout) {
              clearTimeout(loadTimeout);
            }
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            if (document.body.contains(closeBtn)) {
              document.body.removeChild(closeBtn);
            }
          };
          closeBtn.onclick = closeHandler;
          
          // iOS'ta iframe yükleme zaman aşımı (5 saniye)
          if (isIOSDevice) {
            loadTimeout = setTimeout(() => {
              closeHandler();
              onError?.('PDF yükleme zaman aşımına uğradı. Lütfen tekrar deneyin.');
            }, 5000);
          }
          
          iframe.onload = () => {
            if (loadTimeout) {
              clearTimeout(loadTimeout);
            }
            onSuccess?.();
          };
          
          iframe.onerror = () => {
            closeHandler();
            onError?.('PDF yüklenirken hata oluştu');
          };
          
          document.body.appendChild(iframe);
          document.body.appendChild(closeBtn);
          
        } catch (e) {
          const errorMsg = e instanceof Error ? e.message : 'Failed to create iframe';
          onError?.(errorMsg);
        }
      };

      reader.onerror = () => {
        onError?.('FileReader error');
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'FileReader method failed';
      console.warn('FileReader method failed:', error);
      onError?.(errorMsg);
    }
  }

  private openViaBlobUrl(blob: Blob, fileName: string, openInNewTab: boolean): boolean {
    try {
      const blobUrl = URL.createObjectURL(blob);
      const isMobileDevice = window.innerWidth <= 768;
      
      if (openInNewTab) {
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          window.location.href = blobUrl;
        } else {
          if (isMobileDevice) {
            setTimeout(() => {
              try {
                if (newWindow.document) {
                  const viewport = newWindow.document.createElement('meta');
                  viewport.name = 'viewport';
                  viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes';
                  if (!newWindow.document.querySelector('meta[name="viewport"]')) {
                    newWindow.document.head.appendChild(viewport);
                  }
                }
              } catch (e) {
                console.warn('Could not set viewport:', e);
              }
            }, 100);
          }
          
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 1000);
        }
      } else {
        window.location.href = blobUrl;
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      }
      
      return true;
    } catch (error) {
      console.warn('Blob URL method failed:', error);
      return false;
    }
  }

  downloadPdf(
    pdfData: Blob | string | ArrayBuffer,
    fileName: string = `document_${Date.now()}.pdf`,
    options: PdfViewOptions = {}
  ): void {
    const isIOSDevice = this.isIOS();
    const blobPromise = this.toBlob(pdfData);

    from(blobPromise).subscribe({
      next: (blob) => {
        try {
          if (isIOSDevice && (navigator as any).share) {
            const file = new File([blob], fileName, { type: 'application/pdf' });
            
            if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
              (navigator as any).share({
                files: [file],
                title: fileName,
              }).catch((err: any) => {
                if (err.name !== 'AbortError') {
                  this.downloadViaLink(blob, fileName);
                }
              });
              return;
            }
          }

          this.downloadViaLink(blob, fileName);
        } catch (error) {
          console.error('Download failed:', error);
          options.onError?.(error instanceof Error ? error.message : 'Download failed');
        }
      },
      error: (error) => {
        console.error('Blob conversion failed:', error);
        options.onError?.(error instanceof Error ? error.message : 'Failed to prepare PDF for download');
      }
    });
  }

  private downloadViaLink(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  }

  private async toBlob(pdfData: Blob | string | ArrayBuffer): Promise<Blob> {
    if (pdfData instanceof Blob) {
      return pdfData;
    }

    if (typeof pdfData === 'string') {
      const base64Data = pdfData.includes(',') ? pdfData.split(',')[1] : pdfData;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: 'application/pdf' });
    }

    if (pdfData instanceof ArrayBuffer) {
      return new Blob([pdfData], { type: 'application/pdf' });
    }

    throw new Error('Unsupported PDF data type');
  }

  createSafePdfUrl(blob: Blob): SafeResourceUrl | null {
    try {
      const blobUrl = URL.createObjectURL(blob);
      return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    } catch (error) {
      console.error('Failed to create safe PDF URL:', error);
      return null;
    }
  }

  revokeBlobUrl(url: string): void {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Failed to revoke blob URL:', error);
    }
  }

  /**
   * Restores state after returning from PDF view (iOS)
   * Call this in your component's ngOnInit or after page load
   */
  restoreIOSState(): void {
    try {
      const stateData = sessionStorage.getItem('pdfViewState');
      if (stateData) {
        const state = JSON.parse(stateData);
        const timeDiff = Date.now() - state.timestamp;
        
        // Sadece son 5 dakika içindeki state'i geri yükle
        if (timeDiff < 5 * 60 * 1000) {
          console.log('Restoring PDF view state:', state);
          // State'i geri yükleme işlemi burada yapılabilir
          // Örnek: component state'i, scroll position, vb.
        }
        
        // State'i temizle
        sessionStorage.removeItem('pdfViewState');
      }
    } catch (error) {
      console.warn('Failed to restore iOS state:', error);
    }
  }

  fetchPdfFromServer(
    url: string,
    headers?: { [key: string]: string }
  ): Observable<Blob> {
    const httpHeaders = new HttpHeaders(headers);
    
    return this.http.get(url, {
      headers: httpHeaders,
      responseType: 'blob',
      observe: 'body'
    }).pipe(
      catchError((error) => {
        console.error('Failed to fetch PDF from server:', error);
        throw error;
      })
    );
  }
}

